import React, { useEffect, useMemo, useState } from 'react';
import toast from 'react-hot-toast';
import { useMutation, useQuery, useQueryClient } from 'react-query';
import {
  AlertTriangle,
  CalendarDays,
  CheckCircle,
  CircleDollarSign,
  Clock,
  CreditCard,
  Mail,
  MessageSquare,
  Play,
  Receipt,
  RefreshCw,
  RotateCcw,
  Smartphone,
  X,
} from 'lucide-react';
import api from '../../services/api';

const COMMAND_LABELS = {
  send_due_reminder: '납기 안내',
  send_overdue_notice: '미납 안내',
  send_suspend_warning: '정지 경고',
  suspend_service: '서비스 정지',
  unsuspend_service: '서비스 재개',
  mark_paid: '수납 처리',
  extend_grace_period: '유예 연장',
};

const STATUS_LABELS = {
  active: '정상',
  grace: '유예',
  warning: '경고',
  suspended: '정지',
  closed: '종료',
};

const STATUS_STYLES = {
  active: 'bg-green-100 text-green-800',
  grace: 'bg-yellow-100 text-yellow-800',
  warning: 'bg-orange-100 text-orange-800',
  suspended: 'bg-red-100 text-red-800',
  closed: 'bg-gray-100 text-gray-800',
};

const BILL_STATUS_LABELS = {
  paid: '입금완료',
  overdue: '연체',
  issued: '청구완료',
  pending: '대기',
  cancelled: '취소',
};

const BILL_STATUS_STYLES = {
  paid: 'bg-green-100 text-green-800',
  overdue: 'bg-red-100 text-red-800',
  issued: 'bg-blue-100 text-blue-800',
  pending: 'bg-yellow-100 text-yellow-800',
  cancelled: 'bg-gray-100 text-gray-700',
};

const CHANNEL_OPTIONS = [
  { value: 'internal_message', label: '쪽지', icon: MessageSquare, description: '서비스 내부 쪽지함으로 발송' },
  { value: 'sms', label: 'SMS', icon: Smartphone, description: '등록된 휴대폰 번호로 문자 발송' },
  { value: 'email', label: '이메일', icon: Mail, description: '등록된 이메일 주소로 메일 발송' },
];

const getToday = () => new Date().toISOString().slice(0, 10);
const getCurrentMonth = () => new Date().toISOString().slice(0, 7);

const SubscriptionBillingManagement = ({ companies = [] }) => {
  const queryClient = useQueryClient();
  const [billingMonth, setBillingMonth] = useState(getCurrentMonth());
  const [runDate, setRunDate] = useState(getToday());
  const [selectedAgencyId, setSelectedAgencyId] = useState(null);
  const [historyAgency, setHistoryAgency] = useState(null);
  const [notificationChannels, setNotificationChannels] = useState(['internal_message']);

  useEffect(() => {
    if (!selectedAgencyId && companies.length > 0) {
      setSelectedAgencyId(companies[0].id);
    }
  }, [companies, selectedAgencyId]);

  const statusQuery = useQuery(
    ['subscription-billing-statuses', companies.map((company) => company.id).join(',')],
    async () => {
      const results = await Promise.all(
        companies.map(async (company) => {
          const response = await api.get(`/subscription-control/billing-status/${company.id}`);
          return {
            company,
            status: response?.status || null,
          };
        })
      );
      return results;
    },
    {
      enabled: companies.length > 0,
      retry: false,
      onError: (error) => {
        toast.error(error.response?.data?.message || '구독 상태를 불러오지 못했습니다.');
      },
    }
  );

  const logsQuery = useQuery(
    ['subscription-billing-logs', selectedAgencyId],
    () => api.get(`/subscription-control/command-logs/${selectedAgencyId}?limit=20`),
    {
      enabled: !!selectedAgencyId,
      retry: false,
      onError: (error) => {
        toast.error(error.response?.data?.message || '명령 로그를 불러오지 못했습니다.');
      },
    }
  );

  const historyQuery = useQuery(
    ['subscription-billing-history', historyAgency?.id],
    () => api.get(`/subscription-control/billing-history/${historyAgency.id}?limit=24`),
    {
      enabled: !!historyAgency?.id,
      retry: false,
      onError: (error) => {
        toast.error(error.response?.data?.message || '청구 및 입금 이력을 불러오지 못했습니다.');
      },
    }
  );

  const refreshAll = () => {
    queryClient.invalidateQueries('subscription-billing-statuses');
    queryClient.invalidateQueries('subscription-billing-logs');
    queryClient.invalidateQueries('subscription-billing-history');
  };

  const globalActionMutation = useMutation(
    (payload) => api.post(payload.url, payload.body),
    {
      onSuccess: (data) => {
        toast.success(data?.result_message || data?.message || '작업을 완료했습니다.');
        refreshAll();
      },
      onError: (error) => {
        toast.error(error.response?.data?.message || '작업 처리에 실패했습니다.');
      },
    }
  );

  const commandMutation = useMutation(
    ({ agencyId, commandType, payload }) =>
      api.post('/subscription-control/control-command', {
        agency_id: agencyId,
        command_type: commandType,
        payload: payload || {},
      }),
    {
      onSuccess: (data) => {
        toast.success(data?.result_message || '명령을 처리했습니다.');
        refreshAll();
      },
      onError: (error) => {
        toast.error(error.response?.data?.message || '명령 처리에 실패했습니다.');
      },
    }
  );

  const rows = useMemo(() => statusQuery.data || [], [statusQuery.data]);
  const selectedCompanyLogs = logsQuery.data?.logs || [];
  const historyBills = historyQuery.data?.bills || [];

  const summary = useMemo(
    () =>
      rows.reduce(
        (acc, row) => {
          const currentStatus = row.status?.current_status;
          if (currentStatus === 'active') acc.active += 1;
          if (currentStatus === 'grace') acc.grace += 1;
          if (currentStatus === 'warning') acc.warning += 1;
          if (currentStatus === 'suspended') acc.suspended += 1;
          acc.unpaidAmount += Number(row.status?.unpaid_amount || 0);
          return acc;
        },
        { active: 0, grace: 0, warning: 0, suspended: 0, unpaidAmount: 0 }
      ),
    [rows]
  );

  const buildNotificationPayload = (basePayload = {}) => ({
    ...basePayload,
    notification_channels: notificationChannels,
  });

  const ensureChannelsSelected = () => {
    if (notificationChannels.length > 0) return true;
    toast.error('최소 1개 이상의 발송 채널을 선택해주세요.');
    return false;
  };

  const toggleChannel = (channel) => {
    setNotificationChannels((prev) => {
      if (prev.includes(channel)) {
        if (prev.length === 1) {
          toast.error('최소 1개 이상의 발송 채널을 유지해야 합니다.');
          return prev;
        }
        return prev.filter((item) => item !== channel);
      }
      return [...prev, channel];
    });
  };

  const handleGlobalGenerateCycles = () => {
    globalActionMutation.mutate({
      url: '/subscription-control/generate-billing-cycles',
      body: { billing_month: billingMonth },
    });
  };

  const handleGlobalRunAutomation = () => {
    if (!ensureChannelsSelected()) return;
    globalActionMutation.mutate({
      url: '/subscription-control/run-daily-automation',
      body: {
        run_date: runDate,
        notification_channels: notificationChannels,
      },
    });
  };

  const handleCommand = (agencyId, commandType, payload) => {
    if (!ensureChannelsSelected()) return;
    commandMutation.mutate({
      agencyId,
      commandType,
      payload: buildNotificationPayload(payload),
    });
  };

  const handleMarkPaid = (agencyId) => {
    const confirmed = window.confirm('해당 가입자의 미납 구독 청구를 모두 수납 처리할까요?');
    if (!confirmed) return;

    handleCommand(agencyId, 'mark_paid', {
      payment_date: getToday(),
      payment_method: 'manual_admin',
    });
  };

  const handleToggleService = (row) => {
    if (!row?.status) return;

    const isSuspended = row.status.current_status === 'suspended';
    const commandType = isSuspended ? 'unsuspend_service' : 'suspend_service';
    const confirmed = window.confirm(
      isSuspended
        ? '이 가입자의 서비스를 다시 재개할까요?'
        : '이 가입자의 서비스를 정지할까요?'
    );
    if (!confirmed) return;

    handleCommand(row.company.id, commandType, {});
  };

  const handleOpenHistory = (company) => {
    setSelectedAgencyId(company.id);
    setHistoryAgency(company);
  };

  const handleCloseHistory = () => {
    setHistoryAgency(null);
  };

  return (
    <div className="space-y-6">
      <div className="grid grid-cols-1 gap-4 md:grid-cols-2 xl:grid-cols-4">
        <SummaryCard title="정상" value={summary.active} icon={CheckCircle} iconClass="text-green-500" />
        <SummaryCard title="유예" value={summary.grace} icon={Clock} iconClass="text-yellow-500" />
        <SummaryCard title="경고" value={summary.warning} icon={AlertTriangle} iconClass="text-orange-500" />
        <SummaryCard title="정지" value={summary.suspended} icon={CreditCard} iconClass="text-red-500" />
      </div>

      <div className="grid grid-cols-1 gap-4 xl:grid-cols-[2fr,1fr]">
        <div className="rounded-lg border border-gray-200 bg-white p-5 shadow-sm">
          <div className="flex flex-col gap-4 md:flex-row md:items-end md:justify-between">
            <div>
              <h2 className="text-lg font-semibold text-gray-900">구독 배치 제어</h2>
              <p className="mt-1 text-sm text-gray-500">
                월 청구 생성과 일일 자동 알림을 superadmin 화면에서 직접 실행할 수 있습니다.
              </p>
            </div>
            <button
              type="button"
              onClick={refreshAll}
              className="inline-flex items-center justify-center rounded-md border border-gray-300 px-3 py-2 text-sm font-medium text-gray-700 hover:bg-gray-50"
            >
              <RefreshCw className="mr-2 h-4 w-4" />
              새로고침
            </button>
          </div>

          <div className="mt-5 rounded-xl border border-gray-200 bg-gray-50 p-4">
            <div className="flex items-center justify-between gap-3">
              <div>
                <h3 className="text-sm font-semibold text-gray-900">발송 채널 선택</h3>
                <p className="mt-1 text-xs text-gray-500">
                  수동 처리와 자동화 실행 시 아래에서 선택한 채널로 동시에 발송합니다.
                </p>
              </div>
              <div className="text-xs font-medium text-primary-700">
                선택됨 {notificationChannels.length}개
              </div>
            </div>

            <div className="mt-4 grid grid-cols-1 gap-3 md:grid-cols-3">
              {CHANNEL_OPTIONS.map((option) => {
                const checked = notificationChannels.includes(option.value);
                const Icon = option.icon;

                return (
                  <label
                    key={option.value}
                    className={`flex cursor-pointer items-start gap-3 rounded-lg border px-4 py-3 transition ${
                      checked
                        ? 'border-primary-300 bg-primary-50'
                        : 'border-gray-200 bg-white hover:border-gray-300'
                    }`}
                  >
                    <input
                      type="checkbox"
                      checked={checked}
                      onChange={() => toggleChannel(option.value)}
                      className="mt-1 h-4 w-4 rounded border-gray-300 text-primary-600 focus:ring-primary-500"
                    />
                    <div className="flex-1">
                      <div className="flex items-center gap-2 text-sm font-semibold text-gray-900">
                        <Icon className="h-4 w-4" />
                        {option.label}
                      </div>
                      <div className="mt-1 text-xs text-gray-500">{option.description}</div>
                    </div>
                  </label>
                );
              })}
            </div>
          </div>

          <div className="mt-5 grid grid-cols-1 gap-4 md:grid-cols-2">
            <div className="rounded-lg border border-gray-200 p-4">
              <label className="block text-sm font-medium text-gray-700">청구 월</label>
              <input
                type="month"
                value={billingMonth}
                onChange={(e) => setBillingMonth(e.target.value)}
                className="mt-2 block w-full rounded-md border border-gray-300 px-3 py-2 text-sm"
              />
              <button
                type="button"
                onClick={handleGlobalGenerateCycles}
                disabled={globalActionMutation.isLoading}
                className="mt-3 inline-flex w-full items-center justify-center rounded-md bg-primary-600 px-3 py-2 text-sm font-medium text-white hover:bg-primary-700 disabled:opacity-50"
              >
                <Play className="mr-2 h-4 w-4" />
                월 청구 생성
              </button>
            </div>

            <div className="rounded-lg border border-gray-200 p-4">
              <label className="block text-sm font-medium text-gray-700">자동화 실행일</label>
              <input
                type="date"
                value={runDate}
                onChange={(e) => setRunDate(e.target.value)}
                className="mt-2 block w-full rounded-md border border-gray-300 px-3 py-2 text-sm"
              />
              <button
                type="button"
                onClick={handleGlobalRunAutomation}
                disabled={globalActionMutation.isLoading}
                className="mt-3 inline-flex w-full items-center justify-center rounded-md bg-gray-900 px-3 py-2 text-sm font-medium text-white hover:bg-black disabled:opacity-50"
              >
                <RotateCcw className="mr-2 h-4 w-4" />
                일일 자동화 실행
              </button>
            </div>
          </div>

          <div className="mt-4 rounded-lg bg-gray-50 px-4 py-3 text-sm text-gray-700">
            총 미납 구독료 <span className="font-semibold">{summary.unpaidAmount.toLocaleString()}원</span>
          </div>
        </div>

        <div className="rounded-lg border border-gray-200 bg-white p-5 shadow-sm">
          <h2 className="text-lg font-semibold text-gray-900">최근 명령 로그</h2>
          <p className="mt-1 text-sm text-gray-500">
            {selectedAgencyId
              ? `가입자 ID ${selectedAgencyId} 기준 최근 20건`
              : '가입자를 선택하면 로그를 확인할 수 있습니다.'}
          </p>

          <div className="mt-4 space-y-3">
            {logsQuery.isLoading ? (
              <p className="text-sm text-gray-500">로그를 불러오는 중입니다.</p>
            ) : selectedCompanyLogs.length === 0 ? (
              <p className="text-sm text-gray-500">표시할 로그가 없습니다.</p>
            ) : (
              selectedCompanyLogs.map((log) => (
                <div key={log.id} className="rounded-md border border-gray-200 px-3 py-2">
                  <div className="flex items-center justify-between gap-3">
                    <span className="text-sm font-medium text-gray-900">
                      {COMMAND_LABELS[log.command_type] || log.command_type}
                    </span>
                    <span className="text-xs text-gray-500">{formatDateTime(log.requested_at)}</span>
                  </div>
                  <p className="mt-1 text-xs text-gray-600">{log.result_message || '-'}</p>
                </div>
              ))
            )}
          </div>
        </div>
      </div>

      <div className="overflow-hidden rounded-lg border border-gray-200 bg-white shadow-sm">
        <div className="overflow-x-auto">
          <table className="min-w-full divide-y divide-gray-200">
            <thead className="bg-gray-50">
              <tr>
                <th className="px-4 py-3 text-left text-xs font-medium uppercase tracking-wider text-gray-500">가입자명</th>
                <th className="px-4 py-3 text-left text-xs font-medium uppercase tracking-wider text-gray-500">상태</th>
                <th className="px-4 py-3 text-left text-xs font-medium uppercase tracking-wider text-gray-500">다음 납기</th>
                <th className="px-4 py-3 text-left text-xs font-medium uppercase tracking-wider text-gray-500">연체일</th>
                <th className="px-4 py-3 text-left text-xs font-medium uppercase tracking-wider text-gray-500">미납액</th>
                <th className="px-4 py-3 text-left text-xs font-medium uppercase tracking-wider text-gray-500">추천 작업</th>
                <th className="px-4 py-3 text-right text-xs font-medium uppercase tracking-wider text-gray-500">실행</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-200 bg-white">
              {statusQuery.isLoading ? (
                <tr>
                  <td colSpan={7} className="px-4 py-8 text-center text-sm text-gray-500">
                    구독 상태를 불러오는 중입니다.
                  </td>
                </tr>
              ) : rows.length === 0 ? (
                <tr>
                  <td colSpan={7} className="px-4 py-8 text-center text-sm text-gray-500">
                    표시할 구독 가입자가 없습니다.
                  </td>
                </tr>
              ) : (
                rows.map((row) => {
                  const currentStatus = row.status?.current_status || 'closed';
                  const recommendedCommand = row.status?.recommended_command;

                  return (
                    <tr
                      key={row.company.id}
                      className={`cursor-pointer hover:bg-gray-50 ${
                        selectedAgencyId === row.company.id ? 'bg-primary-50' : ''
                      }`}
                      onClick={() => setSelectedAgencyId(row.company.id)}
                    >
                      <td className="px-4 py-4">
                        <button
                          type="button"
                          onClick={(event) => {
                            event.stopPropagation();
                            handleOpenHistory(row.company);
                          }}
                          className="text-left text-sm font-semibold text-primary-700 hover:text-primary-800 hover:underline"
                        >
                          {row.company.name}
                        </button>
                        <div className="text-xs text-gray-500">
                          #{row.company.id} | {row.company.subscription_plan || '-'}
                        </div>
                      </td>
                      <td className="px-4 py-4">
                        <span
                          className={`inline-flex rounded-full px-2.5 py-0.5 text-xs font-medium ${
                            STATUS_STYLES[currentStatus] || STATUS_STYLES.closed
                          }`}
                        >
                          {STATUS_LABELS[currentStatus] || currentStatus}
                        </span>
                      </td>
                      <td className="px-4 py-4 text-sm text-gray-700">{row.status?.next_due_date || '-'}</td>
                      <td className="px-4 py-4 text-sm text-gray-700">{row.status?.overdue_days || 0}일</td>
                      <td className="px-4 py-4 text-sm font-medium text-gray-900">
                        {Number(row.status?.unpaid_amount || 0).toLocaleString()}원
                      </td>
                      <td className="px-4 py-4 text-sm text-gray-700">
                        {recommendedCommand ? COMMAND_LABELS[recommendedCommand] || recommendedCommand : '-'}
                      </td>
                      <td className="px-4 py-4">
                        <div className="flex justify-end gap-2">
                          {recommendedCommand && (
                            <button
                              type="button"
                              onClick={(event) => {
                                event.stopPropagation();
                                handleCommand(row.company.id, recommendedCommand, { only_unsent: true });
                              }}
                              className="rounded-md border border-primary-200 px-2.5 py-1.5 text-xs font-medium text-primary-700 hover:bg-primary-50"
                            >
                              추천 실행
                            </button>
                          )}
                          <button
                            type="button"
                            onClick={(event) => {
                              event.stopPropagation();
                              handleMarkPaid(row.company.id);
                            }}
                            disabled={Number(row.status?.unpaid_amount || 0) <= 0}
                            className="rounded-md border border-gray-300 px-2.5 py-1.5 text-xs font-medium text-gray-700 hover:bg-gray-50 disabled:cursor-not-allowed disabled:opacity-40"
                          >
                            수납 처리
                          </button>
                          <button
                            type="button"
                            onClick={(event) => {
                              event.stopPropagation();
                              handleToggleService(row);
                            }}
                            className="rounded-md border border-gray-300 px-2.5 py-1.5 text-xs font-medium text-gray-700 hover:bg-gray-50"
                          >
                            {currentStatus === 'suspended' ? '재개' : '정지'}
                          </button>
                        </div>
                      </td>
                    </tr>
                  );
                })
              )}
            </tbody>
          </table>
        </div>
      </div>

      {historyAgency && (
        <BillingHistoryModal
          company={historyAgency}
          bills={historyBills}
          isLoading={historyQuery.isLoading}
          onClose={handleCloseHistory}
        />
      )}
    </div>
  );
};

const SummaryCard = ({ title, value, icon: Icon, iconClass }) => (
  <div className="rounded-lg border border-gray-200 bg-white p-5 shadow-sm">
    <div className="flex items-center">
      <div className="flex-shrink-0">
        <Icon className={`h-6 w-6 ${iconClass}`} />
      </div>
      <div className="ml-4">
        <p className="text-sm font-medium text-gray-500">{title}</p>
        <p className="mt-1 text-2xl font-semibold text-gray-900">{value}</p>
      </div>
    </div>
  </div>
);

const BillingHistoryModal = ({ company, bills, isLoading, onClose }) => {
  const totalBilledAmount = bills.reduce((sum, bill) => sum + Number(bill.total_amount || 0), 0);
  const totalPaidAmount = bills
    .filter((bill) => bill.status === 'paid')
    .reduce((sum, bill) => sum + Number(bill.total_amount || 0), 0);

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-gray-900/50 px-4 py-6">
      <div className="flex max-h-[90vh] w-full max-w-5xl flex-col overflow-hidden rounded-2xl bg-white shadow-2xl">
        <div className="flex items-start justify-between border-b border-gray-200 px-6 py-5">
          <div>
            <h3 className="text-xl font-semibold text-gray-900">{company.name}</h3>
            <p className="mt-1 text-sm text-gray-500">구독료 청구 및 입금 이력</p>
          </div>
          <button
            type="button"
            onClick={onClose}
            className="rounded-md p-2 text-gray-500 hover:bg-gray-100 hover:text-gray-700"
          >
            <X className="h-5 w-5" />
          </button>
        </div>

        <div className="grid grid-cols-1 gap-4 border-b border-gray-200 bg-gray-50 px-6 py-4 md:grid-cols-3">
          <HistoryStatCard icon={Receipt} label="총 청구 건수" value={`${bills.length}건`} />
          <HistoryStatCard icon={CircleDollarSign} label="누적 청구액" value={`${totalBilledAmount.toLocaleString()}원`} />
          <HistoryStatCard icon={CreditCard} label="누적 입금액" value={`${totalPaidAmount.toLocaleString()}원`} />
        </div>

        <div className="overflow-auto px-6 py-5">
          {isLoading ? (
            <div className="py-12 text-center text-sm text-gray-500">청구 및 입금 이력을 불러오는 중입니다.</div>
          ) : bills.length === 0 ? (
            <div className="rounded-xl border border-dashed border-gray-300 px-6 py-12 text-center text-sm text-gray-500">
              아직 생성된 구독 청구 이력이 없습니다.
            </div>
          ) : (
            <div className="space-y-3">
              {bills.map((bill) => (
                <div key={bill.id} className="rounded-xl border border-gray-200 px-4 py-4">
                  <div className="flex flex-col gap-3 lg:flex-row lg:items-start lg:justify-between">
                    <div className="space-y-2">
                      <div className="flex flex-wrap items-center gap-2">
                        <span className="text-base font-semibold text-gray-900">{bill.billing_period}</span>
                        <span
                          className={`inline-flex rounded-full px-2.5 py-0.5 text-xs font-medium ${
                            BILL_STATUS_STYLES[bill.status] || BILL_STATUS_STYLES.pending
                          }`}
                        >
                          {BILL_STATUS_LABELS[bill.status] || bill.status || '-'}
                        </span>
                      </div>
                      <div className="text-sm text-gray-600">청구번호 {bill.bill_number}</div>
                    </div>

                    <div className="text-left lg:text-right">
                      <div className="text-lg font-semibold text-gray-900">
                        {Number(bill.total_amount || 0).toLocaleString()}원
                      </div>
                      <div className="text-xs text-gray-500">
                        할인 {Number(bill.discount_amount || 0).toLocaleString()}원
                      </div>
                    </div>
                  </div>

                  <div className="mt-4 grid grid-cols-1 gap-3 text-sm text-gray-700 md:grid-cols-2 xl:grid-cols-4">
                    <HistoryInfo label="청구일" value={formatDate(bill.billing_date)} icon={CalendarDays} />
                    <HistoryInfo label="납기일" value={formatDate(bill.due_date)} icon={CalendarDays} />
                    <HistoryInfo label="입금일" value={formatDateTime(bill.paid_at)} icon={CreditCard} />
                    <HistoryInfo label="입금수단" value={bill.payment_method || '-'} icon={CircleDollarSign} />
                  </div>

                  {bill.payment_reference && (
                    <div className="mt-3 text-xs text-gray-500">입금참조 {bill.payment_reference}</div>
                  )}
                  {bill.notes && <div className="mt-2 text-xs text-gray-500">비고 {bill.notes}</div>}
                </div>
              ))}
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

const HistoryStatCard = ({ icon: Icon, label, value }) => (
  <div className="rounded-xl border border-gray-200 bg-white px-4 py-4">
    <div className="flex items-center gap-3">
      <div className="rounded-lg bg-primary-50 p-2 text-primary-700">
        <Icon className="h-5 w-5" />
      </div>
      <div>
        <div className="text-xs font-medium text-gray-500">{label}</div>
        <div className="mt-1 text-base font-semibold text-gray-900">{value}</div>
      </div>
    </div>
  </div>
);

const HistoryInfo = ({ label, value, icon: Icon }) => (
  <div className="rounded-lg bg-gray-50 px-3 py-3">
    <div className="flex items-center gap-2 text-xs font-medium text-gray-500">
      <Icon className="h-4 w-4" />
      {label}
    </div>
    <div className="mt-1 text-sm font-medium text-gray-900">{value || '-'}</div>
  </div>
);

const formatDate = (value) => {
  if (!value) return '-';
  try {
    return new Date(value).toLocaleDateString('ko-KR');
  } catch (error) {
    return value;
  }
};

const formatDateTime = (value) => {
  if (!value) return '-';
  try {
    return new Date(value).toLocaleString('ko-KR');
  } catch (error) {
    return value;
  }
};

export default SubscriptionBillingManagement;
